import React, { Component, ReactNode, ErrorInfo } from 'react';
import { AppError, ErrorBoundaryState, ErrorBoundaryProps, ErrorType, ErrorSeverity, PerformanceMetrics } from '../types/monitoring';

/**
 * Enhanced ErrorBoundary - A React error boundary component with TypeScript support,
 * performance monitoring, and comprehensive error handling.
 *
 * Features:
 * - Full TypeScript support with comprehensive type safety
 * - Enhanced accessibility support with ARIA attributes
 * - Performance monitoring integration
 * - Detailed error tracking and reporting
 * - Retry functionality with exponential backoff
 * - Customizable error messages and fallback UI
 * - Error categorization and severity levels
 * - Integration with external error reporting services
 * - Memory leak prevention and cleanup
 * - Internationalization support
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private performanceStart: number;
  private retryTimeouts: NodeJS.Timeout[] = [];

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.performanceStart = performance.now();
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorDetails: null,
      boundaryId: props.boundaryId || `error-boundary-${Date.now()}`,
      retryCount: 0,
      maxRetries: props.maxRetries || 3,
    };
  }

  /**
   * Static method called when an error is thrown during rendering
   * Updates state to trigger fallback UI rendering
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Lifecycle method called when an error is caught
   * Handles error logging, performance monitoring, and calls onError callback
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const errorDetails = this.createEnhancedError(error, errorInfo);
    
    this.setState({ 
      errorInfo,
      errorDetails 
    });

    // Record performance metrics
    this.recordErrorPerformance(errorDetails);

    // Log error in development mode
    if (process.env.NODE_ENV !== 'production') {
      console.group(`🚨 ErrorBoundary: ${this.state.boundaryId}`);
      console.error('Error caught:', error);
      console.error('Error info:', errorInfo);
      console.error('Enhanced error details:', errorDetails);
      console.groupEnd();
    }

    // Call external error logging callback if provided
    if (this.props.onError) {
      this.props.onError(errorDetails, errorInfo);
    }
  }

  /**
   * Create enhanced error object with additional metadata
   */
  private createEnhancedError(error: Error, errorInfo: ErrorInfo): AppError {
    const errorType = this.categorizeError(error);
    const severity = this.determineSeverity(error, errorType);

    // Create AppError by extending the original error
    const appError = Object.create(error) as AppError;
    
    // Add AppError specific properties
    appError.id = `${this.state.boundaryId}-${Date.now()}`;
    appError.type = errorType;
    appError.severity = severity;
    appError.userMessage = this.getUserFriendlyMessage(errorType, severity);
    appError.context = {
      ...this.props.context,
      componentStack: errorInfo.componentStack,
      boundaryId: this.state.boundaryId,
      retryCount: this.state.retryCount,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
    appError.timestamp = new Date();
    appError.componentName = this.extractComponentName(errorInfo.componentStack);
    appError.sessionId = this.getSessionId();
    appError.metadata = {
      isolate: this.props.isolate,
      maxRetries: this.state.maxRetries,
    };

    return appError;
  }

  /**
   * Categorize error type based on error properties
   */
  private categorizeError(error: Error): ErrorType {
    const message = error.message.toLowerCase();
    
    if (message.includes('network') || message.includes('fetch')) {
      return 'network_error';
    }
    if (message.includes('timeout')) {
      return 'timeout_error';
    }
    if (message.includes('permission') || message.includes('unauthorized')) {
      return 'permission_error';
    }
    if (message.includes('validation') || message.includes('invalid')) {
      return 'validation_error';
    }
    if (message.includes('simulation')) {
      return 'simulation_error';
    }
    if (error.name === 'ChunkLoadError') {
      return 'component_error';
    }
    
    return 'component_error';
  }

  /**
   * Determine error severity based on error type and properties
   */
  private determineSeverity(error: Error, errorType: ErrorType): ErrorSeverity {
    // Critical errors that break core functionality
    if (errorType === 'permission_error' || error.name === 'SecurityError') {
      return 'critical';
    }
    
    // High severity for simulation errors that break learning experience
    if (errorType === 'simulation_error' || errorType === 'network_error') {
      return 'high';
    }
    
    // Medium severity for component errors
    if (errorType === 'component_error') {
      return 'medium';
    }
    
    // Low severity for validation and timeout errors
    return 'low';
  }

  /**
   * Get user-friendly error message based on type and severity
   */
  private getUserFriendlyMessage(errorType: ErrorType, severity: ErrorSeverity): string {
    const messages = {
      network_error: 'Unable to connect to the server. Please check your internet connection and try again.',
      timeout_error: 'The request took too long to complete. Please try again.',
      permission_error: 'You do not have permission to access this content.',
      validation_error: 'There was an issue with the provided information. Please review and try again.',
      simulation_error: 'There was an issue loading the simulation. Please try again.',
      component_error: 'A component failed to load properly. Please refresh the page.',
      unknown_error: 'An unexpected error occurred. Please try again.',
    };

    return messages[errorType] || messages.unknown_error;
  }

  /**
   * Extract component name from component stack
   */
  private extractComponentName(componentStack: string | null | undefined): string {
    if (!componentStack) {
      return 'Unknown';
    }
    const match = componentStack.match(/in (\w+)/);
    return match ? match[1] : 'Unknown';
  }

  /**
   * Get or create session ID
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('error-boundary-session');
    if (!sessionId) {
      sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('error-boundary-session', sessionId);
    }
    return sessionId;
  }

  /**
   * Record performance metrics for error analysis
   */
  private recordErrorPerformance(errorDetails: AppError): void {
    const metrics: Partial<PerformanceMetrics> = {
      componentName: this.extractComponentName(errorDetails.context?.componentStack || ''),
      renderTime: performance.now() - this.performanceStart,
      errorCount: 1,
      startTime: new Date(this.performanceStart),
      lastUpdate: new Date(),
      customMarkers: {
        timeToError: performance.now() - this.performanceStart,
        retryCount: this.state.retryCount,
      },
    };

    // Send to performance monitoring service if available
    if (window.performance && window.performance.mark) {
      window.performance.mark(`error-boundary-${errorDetails.id}`);
    }
  }

  /**
   * Reset error boundary state with retry logic
   */
  private handleRetry = (): void => {
    if (this.state.retryCount >= this.state.maxRetries) {
      return;
    }

    const newRetryCount = this.state.retryCount + 1;
    const delay = Math.pow(2, newRetryCount) * 1000; // Exponential backoff

    const timeout = setTimeout(() => {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        errorDetails: null,
        retryCount: newRetryCount,
      });
    }, delay);

    this.retryTimeouts.push(timeout);
  };

  /**
   * Reset error boundary completely
   */
  private handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorDetails: null,
      retryCount: 0,
    });
  };

  /**
   * Cleanup method to prevent memory leaks
   */
  componentWillUnmount(): void {
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.errorDetails) {
      // Use custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.errorDetails, this.handleRetry);
      }

      const canRetry = this.state.retryCount < this.state.maxRetries;
      const errorDetails = this.state.errorDetails;

      return (
        <div
          className="error-boundary-container p-6 max-w-md mx-auto mt-8 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded-lg shadow-lg"
          role="alert"
          aria-live="assertive"
          data-testid="error-boundary"
        >
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-red-800 dark:text-red-200">
                Something went wrong
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                {errorDetails.userMessage}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {canRetry && (
              <button
                onClick={this.handleRetry}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                aria-label={`Retry (${this.state.maxRetries - this.state.retryCount || undefined} attempts remaining)`}
              >
                Try Again ({this.state.maxRetries - this.state.retryCount} attempts left)
              </button>
            )}
            
            <button
              onClick={this.handleReset}
              className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              aria-label="Reset and reload"
            >
              Reset
            </button>
          </div>

          {/* Error details for debugging (development only) */}
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-4">
              <summary className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200">
                Technical Details
              </summary>
              <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-xs font-mono">
                <p><strong>Error ID:</strong> {errorDetails.id}</p>
                <p><strong>Type:</strong> {errorDetails.type}</p>
                <p><strong>Severity:</strong> {errorDetails.severity}</p>
                <p><strong>Component:</strong> {errorDetails.componentName}</p>
                <p><strong>Message:</strong> {errorDetails.message}</p>
                <p><strong>Retry Count:</strong> {this.state.retryCount}</p>
                <p><strong>Timestamp:</strong> {errorDetails.timestamp.toISOString()}</p>
              </div>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
